import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Droplets, Recycle, Sparkles, ShieldCheck, ArrowRight, Star } from "lucide-react";
import { PLANS } from "@/data/plans";

const HERO = "https://images.pexels.com/photos/7579045/pexels-photo-7579045.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const fade = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.08, duration: 0.5, ease: "easeOut" } }),
};

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden grain">
        <div className="absolute inset-0">
          <img src={HERO} alt="Southern Maryland home" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-background via-background/85 to-background/30" />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent" />
        </div>
        <div className="relative mx-auto max-w-7xl px-5 py-28 lg:px-8 lg:py-40">
          <motion.div initial="hidden" animate="show" variants={fade} className="max-w-2xl">
            <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-sm font-semibold text-white backdrop-blur">
              <Star className="h-4 w-4 fill-current text-yellow-300" /> Southern Maryland's freshest bins
            </span>
            <h1 className="mt-6 font-heading text-5xl font-extrabold leading-none tracking-tighter text-white md:text-6xl">
              Curbside bins that
              <span className="text-primary"> smell like new.</span>
            </h1>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-white/85">
              We come to your curb, blast your trash & recycling bins with hot,
              high-pressure water, then sanitize and deodorize them. Set it once
              and let it renew on schedule.
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <Link
                to="/booking"
                data-testid="hero-book-btn"
                className="inline-flex items-center gap-2 rounded-xl bg-primary px-7 py-3.5 text-base font-bold text-primary-foreground shadow-lg transition-all hover:-translate-y-0.5 hover:brightness-110"
              >
                Book Your Cleaning <ArrowRight className="h-5 w-5" />
              </Link>
              <Link
                to="/how-it-works"
                className="inline-flex items-center gap-2 rounded-xl border border-white/30 bg-white/10 px-7 py-3.5 text-base font-bold text-white backdrop-blur transition-colors hover:bg-white/20"
              >
                How It Works
              </Link>
            </div>
            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm font-semibold text-white/80">
              <span className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-primary" /> Eco-friendly soap</span>
              <span className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-primary" /> Real slot booking</span>
              <span className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-primary" /> Cancel after minimum</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* VALUE BENTO */}
      <section className="mx-auto max-w-7xl px-5 py-24 lg:px-8">
        <div className="max-w-2xl">
          <p className="text-sm font-semibold uppercase tracking-wider text-primary">Why Bins Plus</p>
          <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight md:text-4xl">
            Dirty bins are gross. We make it effortless to fix.
          </h2>
        </div>
        <div className="mt-12 grid gap-5 md:grid-cols-3">
          {[
            { icon: Droplets, title: "Hot-water blast", text: "Up to 200°F pressure washing melts grime, gunk and stuck-on residue." },
            { icon: Sparkles, title: "Sanitize & deodorize", text: "We kill 99% of bacteria and leave a fresh, clean scent behind." },
            { icon: Recycle, title: "Curbside & contained", text: "All wastewater is captured — nothing goes down your storm drain." },
            { icon: ShieldCheck, title: "No overbooking", text: "Real per-slot capacity means we always show up when promised." },
            { icon: CheckCircle2, title: "Founding special", text: "First 3 Monthly Renew visits at just $29.99 for early customers." },
            { icon: Star, title: "Locally owned", text: "Run by your neighbors right here in Southern Maryland." },
          ].map((f, i) => (
            <motion.div
              key={f.title}
              custom={i}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              variants={fade}
              className="rounded-2xl border border-border bg-card p-8 transition-all hover:-translate-y-1 hover:shadow-lg"
            >
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-accent text-accent-foreground">
                <f.icon className="h-6 w-6" />
              </span>
              <h3 className="mt-5 font-heading text-xl font-bold">{f.title}</h3>
              <p className="mt-2 text-muted-foreground">{f.text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* PLANS */}
      <section className="border-y border-border bg-card">
        <div className="mx-auto max-w-7xl px-5 py-24 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-sm font-semibold uppercase tracking-wider text-primary">Simple pricing</p>
            <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight md:text-4xl">
              Pick recurring — or just try us once
            </h2>
            <p className="mt-4 text-muted-foreground">
              Every plan includes 2 bins per visit. Monthly & Quarterly plans auto-renew on schedule with a saved card. Each additional bin adds a small fee. No hidden charges.
            </p>
          </div>
          <div className="mt-14 grid gap-6 lg:grid-cols-3">
            {PLANS.map((p, i) => (
              <motion.div
                key={p.id}
                custom={i}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                variants={fade}
                className={`relative flex flex-col rounded-2xl border p-8 ${
                  p.popular ? "border-primary bg-background shadow-lg" : "border-border bg-background"
                }`}
              >
                {p.popular && (
                  <span className="absolute -top-3 left-8 rounded-full bg-primary px-3 py-1 text-xs font-bold text-primary-foreground">
                    Most Popular
                  </span>
                )}
                <h3 className="font-heading text-xl font-bold">{p.name}</h3>
                <div className="mt-4 flex items-end gap-1">
                  <span className="font-heading text-4xl font-extrabold tracking-tight">${p.price}</span>
                  <span className="mb-1 text-muted-foreground">{p.unit}</span>
                </div>
                <p className="mt-1 text-sm font-semibold text-muted-foreground">{p.cadence} · {p.minimum}</p>
                <p className="mt-4 text-sm text-muted-foreground">{p.blurb}</p>
                <ul className="mt-6 flex-1 space-y-3">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" /> {f}
                    </li>
                  ))}
                </ul>
                <Link
                  to={`/booking?plan=${p.id}`}
                  data-testid={`plan-select-${p.id}`}
                  className={`mt-8 inline-flex items-center justify-center rounded-xl px-6 py-3 text-sm font-bold transition-all hover:-translate-y-0.5 ${
                    p.popular
                      ? "bg-primary text-primary-foreground hover:brightness-110"
                      : "border border-border bg-card hover:bg-accent hover:text-accent-foreground"
                  }`}
                >
                  Choose {p.name}
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-5 py-24 lg:px-8">
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-primary via-primary/80 to-[#0D1B2A] px-8 py-16 text-center shadow-2xl ring-1 ring-secondary/30 lg:px-20">
          <div className="absolute -right-16 -top-16 h-56 w-56 rounded-full bg-secondary/20 blur-3xl" />
          <h2 className="relative font-heading text-3xl font-bold tracking-tight text-white md:text-4xl">
            Ready for bins that don't stink?
          </h2>
          <p className="relative mx-auto mt-4 max-w-xl text-white/85">
            Book in about a minute. Pick your plan, grab a real time slot, and we'll handle the rest.
          </p>
          <Link
            to="/booking"
            data-testid="cta-book-btn"
            className="relative mt-8 inline-flex items-center gap-2 rounded-xl bg-secondary px-8 py-3.5 text-base font-extrabold text-secondary-foreground shadow-[0_0_24px_rgba(57,255,20,0.35)] transition-all hover:-translate-y-0.5 hover:brightness-110"
          >
            Book Now <ArrowRight className="h-5 w-5" />
          </Link>
        </div>
      </section>
    </div>
  );
}
